import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { ApiError, api } from "../lib/api.js";

export type AppRole =
  | "geschaeftsleitung"
  | "bereichsleiter"
  | "stellvertreter"
  | "projektleiter"
  | "admin";

export interface UserRoleAssignment {
  role: AppRole;
  segmentId: string | null;
}

/** Gesetzt, solange eine fremde Sicht übernommen ist („Sicht übernehmen"). */
export interface AssumedView {
  byEmployeeId: string;
  byEmail: string;
  byFullName: string;
  byRoles: UserRoleAssignment[];
  /** Unix-Sekunden. */
  expiresAt: number;
}

export interface SessionUser {
  employeeId: string;
  email: string;
  fullName: string;
  roles: UserRoleAssignment[];
  defaultSegmentId?: string | null;
  /**
   * Wer wirklich handelt. Ist das gesetzt, gehören `employeeId`, `email`,
   * `fullName` und `roles` der **übernommenen** Person — die Oberfläche zeigt
   * deshalb ein dauerhaftes Banner, sonst verwechselt man beide.
   */
  assumedBy?: AssumedView;
}

export function useCurrentUser() {
  const { data, isLoading, isError } = useQuery<SessionUser | null>({
    queryKey: ["auth", "me"],
    queryFn: async () => {
      try {
        return await api.get<SessionUser>("/auth/me");
      } catch (err) {
        // Nicht eingeloggt → null, nicht als Fehler zurückgeben.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
  });

  return {
    user: data ?? null,
    isLoading,
    isError,
  };
}

export function useLogout() {
  const queryClient = useQueryClient();
  const router = useRouter();

  return useMutation({
    meta: { silent: true },
    mutationFn: () => api.post("/auth/logout"),
    onSettled: async () => {
      queryClient.setQueryData(["auth", "me"], null);
      await router.navigate({ to: "/login" });
    },
  });
}

/** True, wenn der User mindestens eine der angegebenen Rollen hat. */
export function hasRole(
  user: SessionUser | null,
  roles: AppRole | AppRole[],
): boolean {
  if (!user) return false;
  const wanted = Array.isArray(roles) ? roles : [roles];
  return user.roles.some((r) => wanted.includes(r.role));
}

/**
 * Welche Version gerade läuft — Release-Tag auf Produktion, kurzer Commit-SHA
 * auf Staging, `dev` lokal. Kommt aus `/health/live`, damit es keinen zweiten
 * Endpunkt und keine zweite Wahrheit dafür gibt.
 */
function useHealth() {
  return useQuery({
    queryKey: ["health", "version"],
    queryFn: () =>
      api.get<{ status: string; version: string; environment?: string }>(
        "/health/live",
      ),
    staleTime: Infinity,
    retry: false,
  });
}

export function useAppVersion() {
  return useHealth().data?.version ?? null;
}

/**
 * Läuft die Oberfläche gegen ein Testsystem?
 *
 * **Nur eine Umgebung, die sich ausdrücklich dazu bekennt, gilt als eine.**
 * Fehlt `APP_ENV` (alte API-Fassung, vergessener Eintrag in der
 * Compose-Datei), ist die Antwort `false` — kein Band. Das ist die sichere
 * Richtung: Ein Staging ohne Band wird für Produktion gehalten und man ist zu
 * vorsichtig; ein „Testsystem"-Band über den echten Daten hätte den
 * umgekehrten, gefährlichen Effekt.
 */
export function useIstTestsystem(): boolean {
  const umgebung = useHealth().data?.environment ?? "";
  return umgebung !== "" && umgebung !== "production";
}
